import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Send,
  Settings,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
} from 'lucide-react';
import MomentMindMap from './MomentMindMap';
import NodeWorkflow, { EditInteractionModal } from './NodeWorkflow';
import TechnicalIcon from '../../../../assets/images/icons/Container.png';
import NonTechnicalIcon from '../../../../assets/images/icons/Symbol.png';

const CollapsibleSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-zinc-800 rounded-lg border border-zinc-700">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold text-white"
      >
        <span>{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      {open && (
        <div className="p-4 space-y-3 border-t border-zinc-700">{children}</div>
      )}
    </div>
  );
};

const LabeledInput = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) =>
        onChange(type === 'number' ? Number(e.target.value) : e.target.value)
      }
      className="w-full bg-zinc-900 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
      placeholder={placeholder}
    />
  </div>
);

const LabeledTextArea = ({ label, value, onChange, rows = 3, placeholder }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-zinc-900 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
      placeholder={placeholder}
    />
  </div>
);

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
};

CollapsibleSection.defaultProps = {
  defaultOpen: true,
};

LabeledInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
};

LabeledInput.defaultProps = {
  value: '',
  type: 'text',
  placeholder: '',
};

LabeledTextArea.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
};

LabeledTextArea.defaultProps = {
  value: '',
  rows: 3,
  placeholder: '',
};

export default function MomentEditor({
  chapters,
  searchQuery,
  selectedMoment,
  onSearchChange,
  onMomentSelect,
  moment,
  activeTab,
  isTechnicalMode,
  onTabChange,
  onModeChange,
  onMomentUpdate,
  onPublish,
  onSaveInteraction,
}) {
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'general', label: 'General' },
    { id: 'interaction', label: 'Interaction' },
    { id: 'beforeTouch', label: 'Before Touch' },
    { id: 'afterTouch', label: 'After Touch' },
    { id: 'voiceWarnings', label: 'Voice Warnings' },
    { id: 'notes', label: 'Notes' },
  ];

  const [showInteractionModal, setShowInteractionModal] = useState(false);

  const workingMoment = moment || { id: 'placeholder', content: {} };
  const modeKey = isTechnicalMode ? 'technical' : 'nonTechnical';
  const currentContent = useMemo(
    () => workingMoment.content?.[modeKey] || {},
    [workingMoment, modeKey]
  );

  const updateContent = (path, value) => {
    if (!moment) return;
    onMomentUpdate(moment.id, `content.${modeKey}.${path}`, value);
  };

  const overview = currentContent.overview || {};
  const beforeTouch = currentContent.beforeTouch || {};
  const afterTouch = currentContent.afterTouch || {};
  const interaction = currentContent.interaction || {};
  const general = currentContent.general || {};
  const voiceWarnings = currentContent.voiceWarnings || [];

  return (
    <div
      className={`flex-1 max-h-[calc(100vh-180px)] custom-scrollbar overflow-y-auto mt-4 rounded-lg p-6 flex flex-col min-h-0 ${isTechnicalMode ? '' : 'bg-[#292A2E]'}`}
    >
      <div className="flex ml-auto items-center gap-4 mb-6">
        <div className="flex bg-[#444444] px-2 py-1 rounded-md gap-2">
          <button
            onClick={() => onModeChange?.(false)}
            className={`px-4 py-2 flex items-center gap-2 rounded text-sm font-medium transition-colors ${
              isTechnicalMode
                ? ' text-gray-300 hover:bg-zinc-700'
                : 'border-red-600 border-2 text-white'
            }`}
          >
            <img
              src={NonTechnicalIcon}
              className="w-[20px] h-[20px]"
              alt="Non-Technical"
            />
            Non-Technical
          </button>
          <button
            onClick={() => onModeChange?.(true)}
            className={`px-4 py-2 flex items-center justify-center gap-2 rounded text-sm font-medium transition-colors ${
              isTechnicalMode
                ? 'border-red-600 border-2 text-white'
                : ' text-gray-300 hover:bg-zinc-700'
            }`}
          >
            <img
              src={TechnicalIcon}
              className="w-[20px] h-[20px]"
              alt="Technical"
            />
            Technical
          </button>
        </div>
        <div className="flex gap-2">
          {onPublish && (
            <button
              onClick={onPublish}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Send className="w-4 h-4" />
              Publish
            </button>
          )}
          <button className="bg-zinc-800 hover:bg-zinc-700 text-gray-300 p-2 rounded transition-colors">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        className={`rounded-md p-4 ${isTechnicalMode ? '' : 'bg-[#2D2D2D]'}`}
      >
        {!isTechnicalMode && (
          <div className="mb-4">
            <h1 className="text-2xl font-bold mb-1">
              {general.name || 'Untitled Moment'}
            </h1>
            <p className="text-gray-400 text-sm">
              {general.description || 'No description yet.'}
            </p>
          </div>
        )}

        {isTechnicalMode ? (
          // Technical Mode: Show Node Workflow
          <div className="flex-1 flex flex-col min-h-0">
            <NodeWorkflow
              chapters={chapters}
              searchQuery={searchQuery}
              selectedMoment={selectedMoment}
              onSearchChange={onSearchChange}
              onMomentSelect={onMomentSelect}
              onPublish={onPublish}
              isTechnicalMode={isTechnicalMode}
              moment={moment}
              onModeChange={onModeChange}
              onMomentUpdate={onMomentUpdate}
              onSaveInteraction={onSaveInteraction}
            />
          </div>
        ) : (
          // Non-Technical Mode: Show Tabbed Editor
          <>
            <div className="flex  gap-2 mb-6 border-b border-zinc-700">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-red-600 text-white'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex gap-4 items-center">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Moment Name:
                      </label>
                      <p className="text-white text-base">
                        {general.name || 'Untitled Moment'}
                      </p>
                    </div>

                    <div className="flex gap-4 items-center">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Touch Object Query:
                      </label>
                      <p className="text-white text-base">
                        {overview.touchObjectQuery ||
                          interaction.touchObjectQuery ||
                          '—'}
                      </p>
                    </div>

                    <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <h3 className="text-sm font-semibold text-white mb-2">
                        Before Touch:
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            Voice Over:
                          </label>
                          <p className="text-white text-sm">
                            {overview.voiceOver || beforeTouch.voiceOver || '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            Objects:
                          </label>
                          <p className="text-white text-sm">
                            {overview.objects ||
                              beforeTouch.objects?.query ||
                              '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            DisplayText:
                          </label>
                          <p className="text-white text-sm">
                            {overview.displayText ||
                              beforeTouch.displayText ||
                              '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            MetaLayer:
                          </label>
                          <p className="text-white text-sm">
                            {overview.metaLayer ||
                              beforeTouch.objects?.query ||
                              interaction.touchObjectQuery ||
                              '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <h3 className="text-sm font-semibold text-white mb-2">
                        After Touch:
                      </h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            SFX:
                          </label>
                          <p className="text-white text-sm">
                            {afterTouch.sfx || '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="block text-xs text-gray-400 mb-1">
                            MetaLayer:
                          </label>
                          <p className="text-white text-sm">
                            {afterTouch.metaLayer ||
                              interaction.touchObjectQuery ||
                              '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Voice Warnings:
                      </label>
                      {voiceWarnings.length === 0 ? (
                        <p className="text-sm text-gray-400">
                          No voice warnings configured.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {voiceWarnings.map((warning) => (
                            <div
                              key={warning.id}
                              className="bg-zinc-800 rounded-lg p-3"
                            >
                              <p className="text-sm text-white">
                                {warning.message ||
                                  warning.text ||
                                  'Untitled warning'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Trigger:{' '}
                                {warning.touchObjectQuery ||
                                  warning.trigger ||
                                  '—'}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'general' && (
                <div className="space-y-6">
                  <LabeledInput
                    label="Moment Name"
                    value={general.name}
                    onChange={(val) => updateContent('general.name', val)}
                  />
                  <LabeledTextArea
                    label="Description"
                    value={general.description}
                    onChange={(val) =>
                      updateContent('general.description', val)
                    }
                    rows={5}
                    placeholder="Describe what happens in this moment..."
                  />
                </div>
              )}

              {activeTab === 'interaction' && (
                <div className="space-y-4">
                  {/* Summary card */}
                  <div className="bg-zinc-800 rounded-lg p-4 space-y-2 border border-zinc-700">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white">Current Interaction</h3>
                      <button
                        onClick={() => setShowInteractionModal(true)}
                        disabled={!moment}
                        className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-xs px-3 py-1.5 rounded transition-colors"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Edit Interaction
                      </button>
                    </div>
                    <div className="text-xs text-gray-400 space-y-1 pt-1">
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-32">Query / Object:</span>
                        <span className="text-gray-200">{interaction.touchObjectQuery || '—'}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-32">Interaction Type:</span>
                        <span className="text-gray-200">{interaction.interactionType || '—'}</span>
                      </div>
                      {interaction.grabbableName && (
                        <div className="flex gap-2">
                          <span className="text-gray-500 w-32">Grabbable Name:</span>
                          <span className="text-gray-200">{interaction.grabbableName}</span>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-32">Duration:</span>
                        <span className="text-gray-200">{interaction.duration ? `${interaction.duration}s` : '—'}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-32">Required:</span>
                        <span className="text-gray-200">{interaction.required ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>

                  <EditInteractionModal
                    isOpen={showInteractionModal}
                    onClose={() => setShowInteractionModal(false)}
                    moment={moment}
                    onMomentUpdate={onMomentUpdate}
                  />
                </div>
              )}

              {activeTab === 'beforeTouch' && (
                <div className="space-y-6">
                  <CollapsibleSection title="Objects">
                    {['query', 'option', 'spawn'].map((field) => (
                      <LabeledInput
                        key={field}
                        label={field.toUpperCase()}
                        value={beforeTouch.objects?.[field]}
                        onChange={(val) =>
                          updateContent(`beforeTouch.objects.${field}`, val)
                        }
                      />
                    ))}
                  </CollapsibleSection>
                  <CollapsibleSection title="Voice Over">
                    <LabeledTextArea
                      label="Narration"
                      value={beforeTouch.voiceOver}
                      onChange={(val) =>
                        updateContent('beforeTouch.voiceOver', val)
                      }
                      rows={4}
                    />
                  </CollapsibleSection>
                  <CollapsibleSection title="Display Text & Media">
                    <LabeledInput
                      label="Display Text"
                      value={beforeTouch.displayText}
                      onChange={(val) =>
                        updateContent('beforeTouch.displayText', val)
                      }
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Image"
                        value={beforeTouch.image}
                        onChange={(val) =>
                          updateContent('beforeTouch.image', val)
                        }
                      />
                      <LabeledInput
                        label="Video"
                        value={beforeTouch.video}
                        onChange={(val) =>
                          updateContent('beforeTouch.video', val)
                        }
                      />
                    </div>
                  </CollapsibleSection>
                </div>
              )}

              {activeTab === 'afterTouch' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <LabeledInput
                      label="Sound Effect (SFX)"
                      value={afterTouch.sfx}
                      onChange={(val) => updateContent('afterTouch.sfx', val)}
                    />
                    <LabeledInput
                      label="Meta Layer"
                      value={afterTouch.metaLayer}
                      onChange={(val) =>
                        updateContent('afterTouch.metaLayer', val)
                      }
                    />
                  </div>
                  <CollapsibleSection title="Objects">
                    {['query', 'option', 'spawn'].map((field) => (
                      <LabeledInput
                        key={field}
                        label={field.toUpperCase()}
                        value={afterTouch.objects?.[field]}
                        onChange={(val) =>
                          updateContent(`afterTouch.objects.${field}`, val)
                        }
                      />
                    ))}
                  </CollapsibleSection>
                  <CollapsibleSection title="Voice Over & Media">
                    <LabeledTextArea
                      label="Voice Over"
                      value={afterTouch.voiceOver}
                      onChange={(val) =>
                        updateContent('afterTouch.voiceOver', val)
                      }
                      rows={4}
                    />
                    <LabeledInput
                      label="Display Text"
                      value={afterTouch.displayText}
                      onChange={(val) =>
                        updateContent('afterTouch.displayText', val)
                      }
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Image"
                        value={afterTouch.image}
                        onChange={(val) =>
                          updateContent('afterTouch.image', val)
                        }
                      />
                      <LabeledInput
                        label="Video"
                        value={afterTouch.video}
                        onChange={(val) =>
                          updateContent('afterTouch.video', val)
                        }
                      />
                    </div>
                    <LabeledInput
                      label="Visual Effect"
                      value={afterTouch.visualEffect}
                      onChange={(val) =>
                        updateContent('afterTouch.visualEffect', val)
                      }
                    />
                  </CollapsibleSection>
                </div>
              )}

              {activeTab === 'voiceWarnings' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Voice Warnings</h3>
                    <button
                      onClick={() =>
                        updateContent('voiceWarnings', [
                          ...voiceWarnings,
                          {
                            id: Math.random().toString(36),
                            message: '',
                            trigger: '',
                            touchObjectQuery: '',
                          },
                        ])
                      }
                      className="flex items-center gap-2 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded"
                    >
                      <Plus className="w-4 h-4" />
                      Add Warning
                    </button>
                  </div>
                  {voiceWarnings.length === 0 && (
                    <p className="text-sm text-gray-400">
                      No voice warnings configured.
                    </p>
                  )}
                  {voiceWarnings.map((warning, index) => (
                    <div
                      key={warning.id || index}
                      className="bg-zinc-800 rounded-lg p-4 space-y-3 border border-zinc-700"
                    >
                      <div className="flex justify-between items-start">
                        <span className="text-sm font-medium text-white">
                          Warning #{index + 1}
                        </span>
                        <button
                          onClick={() =>
                            updateContent(
                              'voiceWarnings',
                              voiceWarnings.filter((_, i) => i !== index)
                            )
                          }
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <LabeledTextArea
                        label="Message"
                        value={warning.message || warning.text}
                        onChange={(val) => {
                          const next = [...voiceWarnings];
                          next[index] = {
                            ...next[index],
                            message: val,
                            text: val,
                          };
                          updateContent('voiceWarnings', next);
                        }}
                      />
                      <LabeledInput
                        label="Trigger Condition"
                        value={warning.trigger}
                        onChange={(val) => {
                          const next = [...voiceWarnings];
                          next[index] = { ...next[index], trigger: val };
                          updateContent('voiceWarnings', next);
                        }}
                      />
                      <LabeledInput
                        label="Touch Object Query"
                        value={warning.touchObjectQuery}
                        onChange={(val) => {
                          const next = [...voiceWarnings];
                          next[index] = {
                            ...next[index],
                            touchObjectQuery: val,
                          };
                          updateContent('voiceWarnings', next);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Moment Notes</h3>
                  <LabeledTextArea
                    label="Collaboration Notes"
                    value={currentContent.notes}
                    onChange={(val) => updateContent('notes', val)}
                    rows={8}
                    placeholder="Add documentation, reminders, or collaboration notes for the team..."
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

MomentEditor.propTypes = {
  moment: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.object,
  }),
  activeTab: PropTypes.string.isRequired,
  isTechnicalMode: PropTypes.bool.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onMomentUpdate: PropTypes.func.isRequired,
  onPublish: PropTypes.func,
};

MomentEditor.defaultProps = {
  moment: null,
};
